import * as React from "react";
import {useState} from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem,
    Stack,
    TextField,
    Typography
} from "@mui/material";
import Button from "@mui/material/Button";
import ScreenWrapper from "crqlar-react-common/dist/components/ScreenWrapper";
import {useLocalize} from "crqlar-react-common/dist/utils/text.util";
import {useAllServices} from "crqlar-react-common/dist/utils/hooks/services/useServices";
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import {Service} from "crqlar-react-common/dist/models/service/service.model";
import {ServiceStatus} from "crqlar-react-common/dist/models/service/serviceStatus.model";
import {ServiceService} from "crqlar-react-common/dist/services/service/service.service";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ServiceEditDialog from "../services/ServiceEditDialog";
import {useQueryClient} from "react-query";
import {enqueueSnackbar} from "notistack";
import {useReadOnly} from "crqlar-react-common/dist/utils/hooks/users/useReadOnly";

interface Props {
    resource: Resource
    disabled?: boolean
}

const ResourceServicesTab = ({resource, disabled}: Props) => {
    const {t} = useLocalize()
    const queryClient = useQueryClient()
    const readOnly = useReadOnly()
    const allServices = useAllServices()
    const [editingServiceId, setEditingServiceId] = useState<string | null>(null)
    const [assignDialogOpen, setAssignDialogOpen] = useState(false)
    const [selectedService, setSelectedService] = useState<Service | null>(null)
    const [updating, setUpdating] = useState(false)
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
    const [menuService, setMenuService] = useState<Service | null>(null)
    const menuOpen = Boolean(anchorEl)

    const resourceId = resource.id
    const linkedServices = resourceId
        ? allServices.filter(service => service.resourceIds?.includes(resourceId))
        : []
    const unlinkedServices = resourceId
        ? allServices.filter(service => !service.resourceIds?.includes(resourceId))
        : []

    const sameSectorServices = unlinkedServices.filter(service =>
        service.sectorIds?.some(sectorId => resource.sectorIds?.includes(sectorId))
    )
    const sameSectorServiceIds = new Set(sameSectorServices.map(service => service.id))
    const otherSectorServices = unlinkedServices.filter(service => !sameSectorServiceIds.has(service.id))
    const sortedUnlinkedServices = [...sameSectorServices, ...otherSectorServices]
    const canMutate = !disabled && !readOnly
    const controlsDisabled = !canMutate || updating
    const tableColumns = 'minmax(240px, 1fr) 140px 128px'
    const rightCellSx = {
        minWidth: 0,
        textAlign: 'right',
    }

    const formatStatus = (status?: ServiceStatus) => {
        if (status === ServiceStatus.DRAFT) return t('Draft')
        if (status === ServiceStatus.ACTIVE) return t('Active')
        if (status === ServiceStatus.ACTIVE_NOT_BOOKABLE) return t('Active Not Bookable')
        return '-'
    }

    const handleCloseAssignDialog = () => {
        setAssignDialogOpen(false)
        setSelectedService(null)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
        setMenuService(null)
    }

    const persistService = async (service: Service, successMessage = t('Changes saved!')) => {
        setUpdating(true)
        try {
            await ServiceService.updateService(service)
            await queryClient.invalidateQueries('allServices', {exact: false})
            enqueueSnackbar(successMessage, {variant: 'success'})
        } catch {
            enqueueSnackbar(t('Something went wrong'), {variant: 'error'})
        } finally {
            setUpdating(false)
        }
    }

    const handleUnlink = async (service: Service) => {
        if (!resourceId) return
        await persistService({
            ...service,
            resourceIds: (service.resourceIds ?? []).filter(id => id !== resourceId)
        })
    }

    const handleAssign = async () => {
        if (!resourceId || !selectedService) return
        await persistService({
            ...selectedService,
            resourceIds: [...(selectedService.resourceIds ?? []), resourceId]
        })
        handleCloseAssignDialog()
    }

    const renderViewButton = (service: Service) => (
        <Button
            endIcon={<OpenInNewIcon/>}
            sx={{minWidth: 'fit-content'}}
            onClick={() => {
                if (service.id) {
                    window.open(`/system/services/${service.id}`, '_blank')
                }
            }}
        >
            {t('View')}
        </Button>
    )

    const renderMoreButton = (service: Service) => (
        <IconButton
            id={`resource-service-more-menu-${service.id}`}
            aria-controls={menuOpen ? 'resource-service-actions-menu' : undefined}
            aria-haspopup={'true'}
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={(event) => {
                event.stopPropagation()
                setAnchorEl(event.currentTarget)
                setMenuService(service)
            }}
            sx={{
                width: 36.5,
                height: 36.5,
                borderRadius: 1,
            }}
        >
            <MoreVertIcon/>
        </IconButton>
    )

    const renderRowActions = (service: Service) => (
        <Stack direction={'row'} alignItems={'center'} justifyContent={'flex-end'} gap={1}>
            {renderViewButton(service)}
            {canMutate && renderMoreButton(service)}
        </Stack>
    )

    const renderTableHeader = () => (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: tableColumns,
                columnGap: 2,
                width: '100%',
                alignItems: 'center',
                pb: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Typography variant={'body1'} color={'text.secondary'} noWrap>
                {t('Name')}
            </Typography>
            <Typography variant={'body1'} color={'text.secondary'} sx={rightCellSx} noWrap>
                {t('Status')}
            </Typography>
            <Typography variant={'body1'} color={'text.secondary'} sx={rightCellSx} noWrap>
                {t('Actions')}
            </Typography>
        </Box>
    )

    const renderServiceRow = (service: Service) => (
        <Box
            key={service.id}
            sx={{
                display: 'grid',
                gridTemplateColumns: tableColumns,
                columnGap: 2,
                width: '100%',
                alignItems: 'center',
                py: 1.5,
                borderBottom: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.08)',
            }}
        >
            <Typography variant={'body1'} noWrap>
                {service.name || '-'}
            </Typography>

            <Typography variant={'body1'} color={'text.secondary'} sx={rightCellSx} noWrap>
                {formatStatus(service.status)}
            </Typography>

            {renderRowActions(service)}
        </Box>
    )

    return (
        <ScreenWrapper>
            <Stack sx={{width: '100%', maxWidth: '900px', gap: 2, alignSelf: 'center'}}>
                <Alert severity={'info'} sx={{mb: 3}}>
                    {t('Services using this resource')}
                </Alert>

                {!resourceId ? (
                    <Typography variant={'body1'} color={'text.secondary'}>
                        {t('Please save the resource to link services')}
                    </Typography>
                ) : linkedServices.length === 0 ? (
                    <Box
                        sx={{
                            border: '1px dashed',
                            borderColor: 'divider',
                            borderRadius: 1,
                            px: 2,
                            py: 3,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant={'body1'} color={'text.secondary'}>
                            {t('This resource has no services linked yet.')}
                        </Typography>
                    </Box>
                ) : (
                    <Box
                        sx={{
                            overflowX: 'auto',
                            width: '100%',
                        }}
                    >
                        <Stack sx={{minWidth: 560}}>
                            {renderTableHeader()}
                            {linkedServices.map(renderServiceRow)}
                        </Stack>
                    </Box>
                )}

                {resourceId && canMutate && (
                    <Stack direction={'row'} justifyContent={'flex-end'} gap={2} mt={2}>
                        <Button
                            id={'btn-create-linked-service'}
                            variant={'text'}
                            startIcon={<AddIcon/>}
                            disabled={updating}
                            onClick={() => setEditingServiceId('new')}
                        >
                            {t('Create new service')}
                        </Button>

                        <Button
                            id={'btn-attach-existing-service'}
                            variant={'contained'}
                            startIcon={<SearchIcon/>}
                            disabled={updating || sortedUnlinkedServices.length === 0}
                            onClick={() => setAssignDialogOpen(true)}
                        >
                            {t('Attach existing service')}
                        </Button>
                    </Stack>
                )}
            </Stack>

            <Dialog
                open={assignDialogOpen}
                onClose={handleCloseAssignDialog}
                fullWidth
                maxWidth={'xs'}
            >
                <DialogTitle>
                    {t('Attach existing service')}
                </DialogTitle>

                <DialogContent sx={{'&&': {pt: 1}}}>
                    <Autocomplete<Service>
                        id={'attach-existing-service'}
                        options={sortedUnlinkedServices}
                        disabled={controlsDisabled}
                        getOptionLabel={(service) => service.name || ''}
                        value={selectedService}
                        onChange={(_e, value) => setSelectedService(value)}
                        groupBy={(service) =>
                            sameSectorServiceIds.has(service.id)
                                ? t('Services in the selected sectors')
                                : t('Other services')
                        }
                        noOptionsText={t('No services found')}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size={'small'}
                                fullWidth
                                placeholder={t('Search service')}
                            />
                        )}
                        sx={{width: '100%'}}
                    />
                </DialogContent>

                <DialogActions>
                    <Button
                        variant={'contained'}
                        color={'primary'}
                        startIcon={<SaveIcon/>}
                        disabled={!selectedService?.id || controlsDisabled}
                        onClick={handleAssign}
                    >
                        {t('Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                id={'resource-service-actions-menu'}
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                MenuListProps={{
                    'aria-labelledby': 'resource-service-actions-button',
                }}
            >
                <MenuItem
                    disabled={controlsDisabled}
                    onClick={() => {
                        if (menuService) {
                            void handleUnlink(menuService)
                        }
                        handleCloseMenu()
                    }}
                >
                    {t('Remove from resource')}
                </MenuItem>
            </Menu>

            {editingServiceId && (
                <ServiceEditDialog
                    serviceId={editingServiceId}
                    open={!!editingServiceId}
                    onClose={() => setEditingServiceId(null)}
                    defaultResourceId={resourceId}
                />
            )}
        </ScreenWrapper>
    )
}

export default ResourceServicesTab
